<template>
  <div class="stat-wrap">
    <v-overlay absolute :value="loading" :opacity="0.3" :z-index="4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Active filters + map selection, so it's clear what the figures below are
         scoped to. -->
    <div v-if="selection || activeFilters.length" class="filter-summary">
      <!-- Copies the current URL (filters are mirrored to the URL by
           urlFilters.js) so the filtered view can be shared. The label names a
           team link only when a team/superteam is actually scoped — see
           shareLabel. -->
      <button
        type="button"
        class="filter-chip filter-chip--share"
        title="Copy link to this view"
        @click="shareLink"
      >
        <v-icon size="14" class="filter-chip-icon">link</v-icon>
        {{ shareLabel }}
      </button>
      <!-- Map selection chip: removable, takes precedence over the global
           filters because the figures below reflect the selection. -->
      <span v-if="selection" class="filter-chip filter-chip--selection">
        <v-icon size="14" class="filter-chip-icon">{{
          selectionChip.icon
        }}</v-icon>
        {{ selectionChip.label }}
        <v-icon size="14" class="filter-chip-clear" @click="clearSelection">
          close
        </v-icon>
      </span>
      <span v-for="f in activeFilters" :key="f.key" class="filter-chip">
        <v-icon size="14" class="filter-chip-icon">{{ f.icon }}</v-icon>
        {{ f.label }}
        <v-icon size="14" class="filter-chip-clear" @click="clearFilter(f.key)">
          close
        </v-icon>
      </span>
    </div>

    <!-- Headline stats: total striders + countries (the largest figures).
         Hidden for an individual cleanup (always exactly 1 of each). -->
    <div v-if="showHeadline" class="headline-stats">
      <div class="headline-stat">
        <v-img
          contain
          class="headline-icon"
          :src="require('@/assets/home/user.svg')"
        />
        <div class="headline-stat-text">
          <span class="headline-num">{{ usersCount }}</span>
          <span class="headline-caption">{{ stridersCaption }}</span>
        </div>
      </div>
      <div class="headline-stat">
        <v-img
          contain
          class="headline-icon"
          :src="require('@/assets/home/world.svg')"
        />
        <div class="headline-stat-text">
          <span class="headline-num">{{ countriesCount }}</span>
          <span class="headline-caption">Countries</span>
        </div>
      </div>
    </div>
    <!-- Remaining stats, gathered into a compact grid -->
    <div
      class="metric-grid"
      :class="{ 'metric-grid--odd': metrics.length % 2 }"
    >
      <div class="metric" v-for="m in metrics" :key="m.label">
        <v-img contain max-height="36" max-width="36" :src="m.icon" />
        <div class="metric-text">
          <span class="metric-num">{{ m.value }}</span>
          <span class="metric-label">{{ m.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { formatDistance, formatTime, formatWeight } from "../utils/common";
import { mapState, mapActions } from "vuex";

export default {
  props: {
    // The stats object is fetched/owned by Home and passed down (shared with the
    // ItemsBreakdown panel so /dashboards/strides_stats is fetched only once).
    stats: {
      type: Object,
      default: () => ({
        total_distance: 0,
        total_items: [],
        total_time_in_minutes: 0,
        total_strides: 0,
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Weight + per-item-type breakdown for the active map selection, fetched
    // and owned by Home (shared with ItemsBreakdown). Only total_weight is used
    // here, for the Weight metric. null while none/pending.
    selectionStats: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState([
      "startDay",
      "endDay",
      "country",
      "team",
      "superteam",
      "superteamName",
      "totalUsers",
      "totalCountries",
      "stridesUsers",
      "stridesCountries",
      "teamName",
      "userId",
      "userName",
      "selection",
    ]),
    // The headline (Striders / Countries) is meaningless for a single cleanup —
    // always exactly one of each — so hide it in that case. Clusters keep it.
    showHeadline() {
      return !this.selection || this.selection.type === "cluster";
    },
    // The removable chip describing the active map selection.
    selectionChip() {
      if (!this.selection) return null;
      return this.selection.type === "cluster"
        ? { icon: "blur_on", label: "Filtered by Cluster" }
        : { icon: "place", label: "Filtered by Cleanup" };
    },
    // The share button's label. It names a team link only when the view actually
    // is one — a team or superteam is scoped, with or without the other filters
    // alongside it. Anything else (a date range, a country, an individual, a map
    // selection) is still shareable, but calling that a team link would be wrong.
    shareLabel() {
      return this.team || this.superteam
        ? "Share team link"
        : "Share this view";
    },
    // One chip per ACTIVE filter dimension (nothing shown when unfiltered).
    activeFilters() {
      const filters = [];
      if (this.startDay && this.endDay) {
        filters.push({
          key: "date",
          icon: "event",
          label: `${this.startDay} ~ ${this.endDay}`,
        });
      }
      if (this.country) {
        filters.push({ key: "country", icon: "place", label: this.country });
      }
      // Superteam sits above team: when drilled into a child team both chips
      // show (superteam first), so the drill path stays visible.
      if (this.superteam) {
        filters.push({
          key: "superteam",
          icon: "$familyHistory",
          label: this.superteamName || "Superteam",
        });
      }
      if (this.team) {
        filters.push({
          key: "team",
          icon: "groups",
          label: this.teamName || "Team",
        });
      }
      if (this.userId) {
        filters.push({
          key: "individual",
          icon: "person",
          label: this.userName || "Individual",
        });
      }
      return filters;
    },
    // True when any FilterBar filter is active. When filtered, the headline
    // numbers track the filtered strides figures (which loadStrides refreshes on
    // every filter change); unfiltered, they fall back to the global totals.
    isFiltered() {
      return !!(
        this.startDay ||
        this.endDay ||
        this.country ||
        this.team ||
        this.superteam ||
        this.userId
      );
    },
    // Drop the "Worldwide" qualifier for a selection or a specific country.
    stridersCaption() {
      if (this.selection) return "Striders";
      return this.country ? "Striders" : "Striders Worldwide";
    },
    usersCount() {
      const count = this.selection
        ? this.selection.people
        : this.isFiltered
        ? this.stridesUsers
        : this.totalUsers;
      return (count || 0).toLocaleString();
    },
    countriesCount() {
      const count = this.selection
        ? this.selection.countries
        : this.isFiltered
        ? this.stridesCountries
        : this.totalCountries;
      return (count || 0).toLocaleString();
    },
    // The non-headline metrics. For a map selection Strides, Items, Distance and
    // Time are derived client-side from the strides themselves; Weight isn't, so
    // it comes from the backend selection breakdown (selectionStats) and is shown
    // only once that has loaded.
    metrics() {
      if (this.selection) {
        const s = this.selection;
        const metrics = [
          {
            label: "Strides",
            value: this.formatNumber(s.count),
            icon: require("@/assets/home/face.svg"),
          },
          {
            label: "Items",
            value: this.formatNumber(s.items),
            icon: require("@/assets/home/number.svg"),
          },
          {
            label: "Distance",
            value: this.formatDistance(s.distanceMetres),
            icon: require("@/assets/home/loc.svg"),
          },
          {
            label: "Time",
            value: this.formatTime(s.durationMinutes),
            icon: require("@/assets/home/timer.svg"),
          },
        ];
        // Weight comes from the backend breakdown (fetched by Home for the
        // selection); show it only once that's loaded.
        if (this.selectionStats && this.selectionStats.total_weight != null) {
          metrics.splice(2, 0, {
            label: "Weight",
            value: this.formatWeight(this.selectionStats.total_weight),
            icon: require("@/assets/home/weight.svg"),
          });
        }
        return metrics;
      }
      return [
        {
          label: "Strides",
          value: this.formatNumber(this.stats.total_strides),
          icon: require("@/assets/home/face.svg"),
        },
        {
          label: "Items",
          value: this.formatNumber(this.stats.total_items_picked_up),
          icon: require("@/assets/home/number.svg"),
        },
        {
          label: "Weight",
          value: this.formatWeight(this.stats.total_weight),
          icon: require("@/assets/home/weight.svg"),
        },
        {
          label: "Distance",
          value: this.formatDistance(this.stats.total_distance),
          icon: require("@/assets/home/loc.svg"),
        },
        {
          label: "Time",
          value: this.formatTime(this.stats.total_time_in_minutes),
          icon: require("@/assets/home/timer.svg"),
        },
      ];
    },
  },
  methods: {
    ...mapActions([
      "updateSelection",
      "updateStartDay",
      "updateEndDay",
      "updateCountry",
      "updateTeam",
      "updateTeamName",
      "updateTeamSlug",
      "detachSuperteam",
      "updateUserId",
      "updateUserName",
      "showSnackbar",
    ]),
    clearSelection() {
      this.updateSelection(null);
    },
    // Copy the current URL (filters are already mirrored to the query string)
    // to the clipboard and confirm via the global snackbar.
    async shareLink() {
      try {
        await navigator.clipboard.writeText(window.location.href);
        this.showSnackbar({ message: "Link copied ✅", timeout: 1500 });
      } catch (e) {
        // Clipboard write can reject (permissions / insecure context).
        this.showSnackbar({ message: "Couldn't copy link", color: "error" });
      }
    },
    // Remove a single active FilterBar filter from its chip. The FilterBar /
    // TeamFilter dropdowns watch these store values and reset their own display
    // to match (see their storeCountry / storeStartDay / storeTeam watchers).
    clearFilter(key) {
      switch (key) {
        case "date":
          this.updateStartDay(null);
          this.updateEndDay(null);
          break;
        case "country":
          this.updateCountry(null);
          break;
        case "team":
          // Clear only the drilled child team; a parent superteam (if any)
          // stays, returning the dashboard to the superteam-only view.
          this.updateTeam(null);
          this.updateTeamName(null);
          this.updateTeamSlug(null);
          break;
        case "superteam":
          // Leave the superteam but keep a drilled child team, which becomes a
          // standalone team scope. With no child, this is a plain exit to global.
          this.detachSuperteam();
          break;
        case "individual":
          this.updateUserId(null);
          this.updateUserName(null);
          break;
      }
    },
    formatNumber(value) {
      if (!value && value !== 0) return "0";

      // Always round up to the next integer
      const roundedValue = Math.ceil(parseFloat(value));

      // Add commas for thousands
      return roundedValue.toLocaleString("en");
    },

    formatWeight,

    formatDistance,

    formatTime,
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/mixins.scss";
@import "@/styles/variables.scss";
.stat-wrap {
  position: relative;
  padding: 16px;
  min-width: 185px;
  // Fill the card so the pie can take a percentage of the real container height.
  height: 100%;
  display: flex;
  flex-direction: column;
  // Query container for the fluid sizes below: 100cqi = this panel's inner
  // width, so they track the resize handle rather than the viewport.
  // (inline-size only — block-size stays auto, so the pie's % height still works.)
  container-type: inline-size;
}
// Active-filter chips above the headline figures. Block (not flex) layout so
// the share button can float to the right of the FIRST row only: the float
// shortens the first line, and the wrapped chips below it flow full width.
// Inter-chip spacing is therefore margins, not gap; font-size: 0 kills the
// whitespace between the inline-flex chips.
.filter-summary {
  font-size: 0;
  margin-bottom: 4px;

  // Contain the float so the stats below never wrap around it.
  &::after {
    content: "";
    display: block;
    clear: both;
  }

  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0 8px 8px 0;
    padding: 4px 10px;
    background-color: $color-raised;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    color: $color-text-primary;
    white-space: nowrap;
  }
  .filter-chip-icon {
    color: $color-text-primary;
  }
  // The share button reuses the chip shape but is interactive, so it carries the
  // primary gradient (same treatment as the active FilterBar controls).
  .filter-chip--share {
    // Floated (and first in the DOM) so it sits at the right edge of the first
    // chip row; later chips wrap underneath it, not around it.
    float: right;
    margin: 0 0 8px 8px;
    cursor: pointer;
    border: none;
    font-family: inherit;
    background: $color-primary-gradient;
    color: $color-on-primary;
    transition: filter 0.15s ease;

    .filter-chip-icon {
      color: $color-on-primary;
    }

    &:hover {
      filter: brightness(0.94);
    }
  }
  // The map-selection chip stands out from the (passive) filter chips and
  // carries a clickable clear button.
  .filter-chip--selection {
    background-color: var(--v-primary-base);
    color: $color-on-primary;

    .filter-chip-icon {
      color: $color-on-primary;
    }
  }
  .filter-chip-clear {
    color: $color-text-primary;
    cursor: pointer;
    margin-left: 2px;
    border-radius: 50%;

    &:hover {
      background-color: rgba(22, 50, 35, 0.15);
    }
  }
}
// Headline stats — total striders + countries. Two separate cards (like the
// metric cards), keeping the larger headline fonts. The icon and text sizes
// scale fluidly with the PANEL width (container query units), so they respond
// to the resize handle as well as the viewport. 100cqi = the stats panel's
// inner width (see container-type on .stat-wrap). One clamp per property covers
// every width — no per-breakpoint variants needed.
.headline-stats {
  display: grid;
  // Same width-driven behaviour as the metric grid: 2 columns, collapsing to 1
  // when the panel is too narrow for two cards (45% floor caps it at 2).
  grid-template-columns: repeat(auto-fit, minmax(max(150px, 45%), 1fr));
  gap: 8px;

  .headline-stat {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    background-color: $color-raised;
    border-radius: 10px;
    padding: 10px 16px;
    // Query container for the icon clamp below: 100cqi now = this card's inner
    // width rather than the whole panel's.
    container-type: inline-size;
  }
  @include mobile {
    .headline-stat {
      padding: 8px 10px;
    }
  }
  .headline-icon {
    flex: 0 0 auto;
    width: clamp(36px, calc(36px + (100cqi - 150px) * 0.12), 48px);
    height: clamp(36px, calc(36px + (100cqi - 150px) * 0.12), 48px);
    // width: 48px;
    // height: 48px;
  }
  .headline-stat-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
  }
  .headline-num {
    // font-size: clamp(18px, calc(18px + (100cqi - 300px) * 0.05), 24px);
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
  }
  .headline-caption {
    // font-size: clamp(12px, calc(12px + (100cqi - 300px) * 0.02), 15px);
    font-size: 15px;
    line-height: 1.2;
    color: #555;
  }
}
.theme--dark .headline-caption {
  color: var(--v-textMuted-base);
}

// The remaining metrics, in a grid driven by the panel's own width (not the
// viewport — the stats panel is resizable). Each column is at least
// max(150px, 45%) wide: the 45% floor means a 3rd column never fits (3×45% >
// 100%), so it tops out at 2 columns and collapses to 1 once the container is
// too narrow for two 150px columns.
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(max(150px, 45%), 1fr));
  gap: 8px;
  margin-top: 8px;

  .metric {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: start;
    background-color: $color-raised;
    border-radius: 10px;
    padding: 10px;
    gap: 6px;
  }
  @container (max-width: 308px) {
    .metric {
      padding: 4px 10px;
    }
  }
  // With an odd number of metrics the last one (Time) would sit alone in a
  // half-width slot, so span it across the full row to balance the grid. With an
  // even count it fits the 2x2 layout as-is, so we leave it. (In the 1-column
  // layout `1 / -1` is just the single column — a no-op — so gating on parity is
  // harmless there.)
  &.metric-grid--odd .metric:last-child {
    grid-column: 1 / -1;
  }
  .metric-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  // Single-column mode: the grid collapses to 1 column once the panel is too
  // narrow for two 150px tracks (2×150 + 8px gap ≈ 308px). At that point lay the
  // value + label side by side instead of stacked, so the wider cards read as a
  // single row.
  @container (max-width: 308px) {
    .metric-text {
      flex: 1;
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
      gap: 6px;
    }
    // Label first, then the number, when laid out in a row.
    .metric-label {
      order: -1;
    }
  }
  .metric-num {
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
  }
  .metric-label {
    font-size: 15px;
    line-height: 1;
    color: #666;
  }

  @include mobile {
    .metric {
      padding: 10px 6px;
    }
    .metric-num {
      font-size: 16px;
    }
  }
}
.theme--dark .metric-label {
  color: var(--v-textMuted-base);
}
</style>
